import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SiteSelectField } from "@/components/SiteSelectField";
import client, { getErrorMessage } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import ServiceQuantities from "@/pages/serviceTracker/ServiceQuantities";
import { toast } from "sonner";

function toDateInput(v) {
  if (!v) return "";
  return new Date(v).toISOString().slice(0, 10);
}

export default function AssetFormDialog({ asset, sites, onClose, onSaved }) {
  const isEdit = !!asset;
  const { user } = useAuth();
  const [quantities, setQuantities] = useState(null);
  const [machineTypes, setMachineTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [entities, setEntities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    asset_id: asset?.asset_id || "",
    machine_type: asset?.machine_type || "",
    brand: asset?.brand || "",
    capacity: asset?.capacity || "",
    serial_number: asset?.serial_number || "",
    engine_number: asset?.engine_number || "",
    owned_by_entity: asset?.owned_by_entity || "",
    owned_by_other: "",
    assigned_site: asset?.assigned_site || "",
    current_status: asset?.current_status || "",
    purchase_date: toDateInput(asset?.purchase_date),
    remarks: asset?.remarks || "",
  });

  useEffect(() => {
    Promise.all([
      client.get("/asset-masters/machine_type"),
      client.get("/asset-masters/brand"),
      client.get("/asset-masters/asset_status"),
      client.get("/asset-masters/owned_by_entity"),
    ])
      .then(([mt, br, st, en]) => {
        setMachineTypes(mt.data);
        setBrands(br.data);
        setStatuses(st.data);
        setEntities(en.data);
        if (asset && asset.owned_by_entity && !en.data.some((item) => item.label === asset.owned_by_entity)) {
          setForm((f) => ({ ...f, owned_by_entity: "Other", owned_by_other: asset.owned_by_entity }));
        }
      })
      .catch((err) => toast.error(getErrorMessage(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const showQuantities =
    isEdit && asset.active !== false && ["Director", "Manager", "Back Office"].includes(user?.role) && /^\s*piling\s+rig\s*$/i.test(asset.machine_type || "");
  const showOtherInput = form.owned_by_entity === "Other";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.asset_id.trim()) return toast.error("Asset ID is required");
    if (!form.machine_type) return toast.error("Machine Type is required");
    if (!form.brand) return toast.error("Brand is required");
    if (!form.current_status) return toast.error("Current Status is required");
    const ownedBy = showOtherInput ? form.owned_by_other.trim() : form.owned_by_entity;
    if (!ownedBy) return toast.error("Owned By Entity is required");
    setSaving(true);
    const payload = {
      asset_id: form.asset_id.trim(),
      machine_type: form.machine_type,
      brand: form.brand,
      capacity: form.capacity || null,
      serial_number: form.serial_number || null,
      engine_number: form.engine_number || null,
      owned_by_entity: ownedBy,
      assigned_site: form.assigned_site || null,
      current_status: form.current_status,
      purchase_date: form.purchase_date ? new Date(form.purchase_date).toISOString() : null,
      remarks: form.remarks || null,
    };
    try {
      if (isEdit) {
        const res = await client.put(`/assets/${asset.asset_id}`, payload);
        if (showQuantities && quantities) {
          try {
            await client.put(`/service-tracker/assets/${encodeURIComponent(res.data.asset_id)}/quantities`, { quantities });
          } catch (qerr) {
            toast.error(`Asset saved, but quantities were not: ${getErrorMessage(qerr)}`);
          }
        }
        toast.success("Asset updated");
        onSaved(res.data);
      } else {
        const res = await client.post("/assets", payload);
        toast.success(`Asset created — ${res.data.asset_id}`);
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="asset-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Asset — ${asset.asset_id}` : "Add Asset"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Asset ID *</label>
              <Input data-testid="asset-form-id-input" value={form.asset_id} onChange={(e) => set("asset_id", e.target.value)} required />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Machine Type *</label>
              <SearchableSelect testId="asset-form-machine-type-select" value={form.machine_type} onValueChange={(v) => set("machine_type", v)} placeholder="Select"
  options={machineTypes.map((x) => ({ value: x.label, label: x.label, testId: `asset-form-machine-type-option-${x.code}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Brand *</label>
              <SearchableSelect testId="asset-form-brand-select" value={form.brand} onValueChange={(v) => set("brand", v)} placeholder="Select"
  options={brands.map((x) => ({ value: x.label, label: x.label, testId: `asset-form-brand-option-${x.code}` }))} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Capacity / Model</label>
              <Input data-testid="asset-form-capacity-input" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Serial Number</label>
              <Input data-testid="asset-form-serial-input" value={form.serial_number} onChange={(e) => set("serial_number", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Engine Number</label>
              <Input data-testid="asset-form-engine-input" value={form.engine_number} onChange={(e) => set("engine_number", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Owned By Entity *</label>
              <SearchableSelect testId="asset-form-owned-by-select" value={form.owned_by_entity} onValueChange={(v) => set("owned_by_entity", v)} placeholder="Select"
  options={entities.map((x) => ({ value: x.label, label: x.label, testId: `asset-form-owned-by-option-${x.code}` }))} />
              {showOtherInput && (
                <Input
                  className="mt-2"
                  data-testid="asset-form-owned-by-other-input"
                  placeholder="Enter entity name"
                  value={form.owned_by_other}
                  onChange={(e) => set("owned_by_other", e.target.value)}
                />
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Current Status *</label>
              <Select value={form.current_status} onValueChange={(v) => set("current_status", v)}>
                <SelectTrigger data-testid="asset-form-status-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.code} value={s.label} data-testid={`asset-form-status-option-${s.code}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Assigned Site</label>
              <SiteSelectField sites={sites} value={form.assigned_site} onChange={(v) => set("assigned_site", v)} testId="asset-form-site-select" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Purchase Date</label>
              <Input type="date" data-testid="asset-form-purchase-date-input" value={form.purchase_date} onChange={(e) => set("purchase_date", e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Remarks</label>
              <Textarea data-testid="asset-form-remarks-input" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} />
            </div>
            {showQuantities && <ServiceQuantities assetId={asset.asset_id} onChange={setQuantities} />}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="asset-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="asset-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Asset"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
